import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download, Save, Loader2, Share2, Check } from 'lucide-react';
import { FigureCanvas } from './FigureCanvas';
import { CanvasToolbar } from './CanvasToolbar';
import { useFigureCompositionStore, type FigurePanel } from '@/store/figure-composition';
import { ExportDialog } from './ExportDialog';
import { Card } from '@/components/ui/card';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import { useProjectsStore } from '@/store/projects';
import { useFigureEditorUiState } from '@/hooks/useFigureEditorUiState';
import { authorizedFetch } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { usePlotsStore } from '@/store/plots';

interface FigureCanvasWrapperProps {
  onSave?: () => void;
  isSaving?: boolean;
  unsaved?: boolean;
  unsavedLabel?: string;
  isOffline?: boolean;
  isEmpty?: boolean;
}

export function FigureCanvasWrapper({ onSave, isSaving, unsaved, unsavedLabel, isOffline = false, isEmpty = false }: FigureCanvasWrapperProps) {
  // Use granular selectors to prevent re-renders when only figureLegend changes
  // Only subscribe to the parts we actually need for rendering
  const panels = useFigureCompositionStore((state) => state.currentComposition?.panels);
  const canvasSize = useFigureCompositionStore((state) => state.currentComposition?.canvasSize);
  const grid = useFigureCompositionStore((state) => state.currentComposition?.grid);
  const theme = useFigureCompositionStore((state) => state.currentComposition?.theme);
  const title = useFigureCompositionStore((state) => state.currentComposition?.title);
  const caption = useFigureCompositionStore((state) => state.currentComposition?.caption);
  const projectId = useFigureCompositionStore((state) => state.currentComposition?.projectId);
  
  // Memoize currentComposition to prevent unnecessary re-renders of FigureCanvas
  // Only create new object when actual data changes, not when object reference changes
  const currentComposition = useMemo(() => {
    if (!panels || !canvasSize || !grid || !theme) return null;
    return {
      panels,
      canvasSize,
      grid,
      theme,
      title: title || '',
      caption: caption || '',
      projectId: projectId || '',
      // Include other properties that might be needed
      axesSharing: useFigureCompositionStore.getState().currentComposition?.axesSharing,
      figureLegend: useFigureCompositionStore.getState().currentComposition?.figureLegend,
    } as any;
  }, [panels, canvasSize, grid, theme, title, caption, projectId]);
  const applyTemplatePreset = useFigureCompositionStore((state) => state.applyTemplatePreset);
  const selectedPanelIds = useFigureCompositionStore((state) => state.selectedPanelIds);
  const selectedAnnotationId = useFigureCompositionStore((state) => state.selectedAnnotationId);
  const selectedPanelId = useFigureCompositionStore((state) => state.selectedPanelId);
  const clearSelection = useFigureCompositionStore((state) => state.clearSelection);
  const removePanel = useFigureCompositionStore((state) => state.removePanel);
  const duplicatePanels = useFigureCompositionStore((state) => state.duplicatePanels);
  const nudgePanels = useFigureCompositionStore((state) => state.nudgePanels);
  const removeAnnotation = useFigureCompositionStore((state) => state.removeAnnotation);
  const nudgeAnnotation = useFigureCompositionStore((state) => state.nudgeAnnotation);
  const setSelectedAnnotationId = useFigureCompositionStore((state) => state.setSelectedAnnotationId);
  const { currentProject } = useProjectsStore();
  const { go } = useNav();
  const { toast } = useToast();
  const [exportOpen, setExportOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareToken, setShareToken] = useState('');
  const [copiedShare, setCopiedShare] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const copiedShareTimeoutRef = useRef<number | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const { canvasZoom, rulersVisible, previewAtJournalSize, toggleGrid: toggleGridUi } = useFigureEditorUiState();

  // Debug: log initial zoom value
  useEffect(() => {
    console.log('🔍 [FigureCanvasWrapper] Component mounted/updated with zoom:', canvasZoom);
  }, []);

  // Debug: log zoom changes
  useEffect(() => {
    console.log('🔍 [FigureCanvasWrapper] Canvas zoom changed:', canvasZoom, 'previewAtJournalSize:', previewAtJournalSize);
    if (canvasContainerRef.current) {
      const containerStyle = window.getComputedStyle(canvasContainerRef.current);
      const scaledWrapper = canvasContainerRef.current.querySelector('[data-zoom]') as HTMLElement;
      const wrapperStyle = scaledWrapper ? window.getComputedStyle(scaledWrapper) : null;
      const wrapperInlineStyle = scaledWrapper?.style.transform;
      const wrapperDataZoom = scaledWrapper?.getAttribute('data-zoom');
      const wrapperDataPreview = scaledWrapper?.getAttribute('data-preview-mode');
      console.log('🔍 [FigureCanvasWrapper] Container measurements:', {
        containerClientWidth: canvasContainerRef.current.clientWidth,
        containerClientHeight: canvasContainerRef.current.clientHeight,
        containerComputedWidth: containerStyle.width,
        containerComputedHeight: containerStyle.height,
        wrapperInlineTransform: wrapperInlineStyle,
        wrapperComputedTransform: wrapperStyle?.transform,
        wrapperWidth: wrapperStyle?.width,
        wrapperDataZoom,
        wrapperDataPreview,
        zoom: canvasZoom,
        previewAtJournalSize: previewAtJournalSize,
        previewAtJournalSizeType: typeof previewAtJournalSize,
        expectedTransform: previewAtJournalSize ? 'none' : `scale(${canvasZoom})`,
        actualTransformCondition: previewAtJournalSize ? 'PREVIEW MODE - transform disabled' : 'ZOOM MODE - transform should be applied',
      });
    }
  }, [canvasZoom, previewAtJournalSize]);

  // Measure container width for fit-to-width calculation
  useEffect(() => {
    const measureContainer = () => {
      if (canvasContainerRef.current) {
        setContainerWidth(canvasContainerRef.current.clientWidth);
      }
    };

    measureContainer();
    const resizeObserver = new ResizeObserver(measureContainer);
    if (canvasContainerRef.current) {
      resizeObserver.observe(canvasContainerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [currentComposition]);

  const handleCanvasDragOver = (e: React.DragEvent) => {
    // Handle template and plot/image drags on canvas
    const hasLibraryData = e.dataTransfer.types.includes('application/json');
    if (!hasLibraryData) return;
    
    // Check if drag is over a panel - if so, let the panel handle it
    const target = e.target as HTMLElement;
    if (target.closest('[data-panel-id]')) {
      // Let the panel handle plot/image drags
      return;
    }
    
    // Allow drops on empty canvas for both templates and plots/images
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleCanvasDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const currentTarget = e.currentTarget as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;

      const payload = JSON.parse(data);
      
      // Handle template drops on canvas
      if (payload.type === 'template' && payload.templateId) {
        applyTemplatePreset(payload.templateId);
        return;
      }
      
      // Handle plot drops on empty canvas
      if (payload.type === 'plot' && payload.plotId && currentComposition) {
        const addPanel = useFigureCompositionStore.getState().addPanel;
        const updatePanel = useFigureCompositionStore.getState().updatePanel;
        
        // Check if there are existing panels
        const existingPanels = currentComposition.panels;
        
        if (existingPanels.length > 0) {
          // Find the next empty panel (panel without a plotId)
          const emptyPanel = existingPanels.find((p: FigurePanel) => !p.plotId);
          
          if (emptyPanel) {
            // Fill the next empty panel
            // Preserve plotVersionId if present (pinned), otherwise use latest (undefined = follow latest)
            updatePanel(emptyPanel.id, { 
              plotId: payload.plotId, 
              plotVersionId: payload.plotVersionId, // Preserve pinned version, or undefined for latest
            });
            return;
          }
        }
        
        // No empty panels found - create a new panel at drop location
        // Calculate grid position from drop coordinates
        if (canvasContainerRef.current) {
          // Find the grid layout element (it's inside FigureCanvas, which is inside a zoom wrapper)
          const gridLayout = canvasContainerRef.current.querySelector('.react-grid-layout') as HTMLElement;
          
          if (gridLayout) {
            // Get drop coordinates relative to the grid layout
            // getBoundingClientRect() already accounts for CSS transforms, so we get
            // coordinates in the transformed space. We need to convert to the grid's
            // logical coordinate system (before transform).
            const gridRect = gridLayout.getBoundingClientRect();
            const dropX = e.clientX - gridRect.left;
            const dropY = e.clientY - gridRect.top;
            
            // Calculate grid position
            // Grid uses composition.grid.cols columns
            const availableWidthPx = (currentComposition.canvasSize.width / 25.4) * 96;
            const colWidthPx = availableWidthPx / currentComposition.grid.cols;
            const rowHeightPx = currentComposition.grid.rowHeight;
            
            // Account for zoom if not in preview mode
            // The zoom wrapper applies a CSS transform, but getBoundingClientRect() already
            // accounts for it. However, we need coordinates in the grid's logical space.
            // Since the grid layout is inside the zoom wrapper, we need to divide by zoom
            // to get the logical coordinates.
            const effectiveZoom = previewAtJournalSize ? 1 : canvasZoom;
            const logicalDropX = dropX / effectiveZoom;
            const logicalDropY = dropY / effectiveZoom;
            
            // Calculate grid coordinates (accounting for margin between grid items)
            const marginX = currentComposition.grid.margin[0];
            const marginY = currentComposition.grid.margin[1];
            
            // Calculate which grid cell the drop is in
            // Each cell is colWidthPx wide + marginX spacing
            const cellWidth = colWidthPx + marginX;
            const cellHeight = rowHeightPx + marginY;
            
            const gridX = Math.max(0, Math.min(
              currentComposition.grid.cols - 1,
              Math.floor(logicalDropX / cellWidth)
            ));
            const gridY = Math.max(0, Math.floor(logicalDropY / cellHeight));
            
            // Default panel size: full width (12 cols) and 6 rows height
            const gridW = Math.min(12, currentComposition.grid.cols - gridX);
            const gridH = 6;
            
            // Create new panel at drop location
            addPanel({
              x: gridX,
              y: gridY,
              w: gridW,
              h: gridH,
              plotId: payload.plotId,
              // Always use latest version for drags so it matches what the plot editor shows
              plotVersionId: payload.plotVersionId, // Preserve pinned version, or undefined for latest
            });
          } else {
            // Fallback: create panel at default position (0, 0)
            addPanel({
              x: 0,
              y: 0,
              w: 12,
              h: 6,
              plotId: payload.plotId,
              // Always use latest version for drags so it matches what the plot editor shows
              plotVersionId: payload.plotVersionId, // Preserve pinned version, or undefined for latest
            });
          }
        } else {
          // Fallback: create panel at default position
          addPanel({
            x: 0,
            y: 0,
            w: 12,
            h: 6,
            plotId: payload.plotId,
            // Always use latest version for drags so it matches what the plot editor shows
            plotVersionId: undefined,
          });
        }
      }
    } catch (error) {
      console.error('Failed to handle canvas drop:', error);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle shortcuts when typing in inputs
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Handle annotation shortcuts first (higher priority)
      if (selectedAnnotationId && selectedPanelId && currentComposition) {
        const panel = currentComposition.panels.find((p: FigurePanel) => p.id === selectedPanelId);
        const annotation = panel?.annotations?.find((a: { id: string }) => a.id === selectedAnnotationId);

        if (annotation) {
          // Arrow keys: nudge annotation
          if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
            e.preventDefault();
            const delta = e.shiftKey ? 0.05 : 0.01; // Larger step with Shift
            let deltaX = 0;
            let deltaY = 0;

            switch (e.key) {
              case 'ArrowUp':
                deltaY = -delta;
                break;
              case 'ArrowDown':
                deltaY = delta;
                break;
              case 'ArrowLeft':
                deltaX = -delta;
                break;
              case 'ArrowRight':
                deltaX = delta;
                break;
            }

            if (deltaX !== 0 || deltaY !== 0) {
              nudgeAnnotation(selectedPanelId, selectedAnnotationId, deltaX, deltaY);
            }
            return;
          }

          // Delete/Backspace: remove selected annotation
          if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            removeAnnotation(selectedPanelId, selectedAnnotationId);
            setSelectedAnnotationId(null);
            return;
          }

          // Escape: deselect annotation
          if (e.key === 'Escape') {
            e.preventDefault();
            setSelectedAnnotationId(null);
            return;
          }
        }
      }

      // Arrow keys: nudge panels (if no annotation selected)
      if (selectedPanelIds.length > 0 && !selectedAnnotationId && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const delta = e.shiftKey ? 5 : 1;
        let deltaX = 0;
        let deltaY = 0;

        switch (e.key) {
          case 'ArrowUp':
            deltaY = -delta;
            break;
          case 'ArrowDown':
            deltaY = delta;
            break;
          case 'ArrowLeft':
            deltaX = -delta;
            break;
          case 'ArrowRight':
            deltaX = delta;
            break;
        }

        if (deltaX !== 0 || deltaY !== 0) {
          nudgePanels(selectedPanelIds, deltaX, deltaY);
        }
        return;
      }

      // Delete/Backspace: remove selected panels (if no annotation selected)
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedPanelIds.length > 0 && !selectedAnnotationId) {
        e.preventDefault();
        if (confirm(`Delete ${selectedPanelIds.length} panel(s)?`)) {
          selectedPanelIds.forEach((id) => removePanel(id));
          clearSelection();
        }
        return;
      }

      // Ctrl/Cmd + D: duplicate panels
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedPanelIds.length > 0 && !selectedAnnotationId) {
        e.preventDefault();
        duplicatePanels(selectedPanelIds);
        return;
      }

      // Ctrl/Cmd + G: toggle grid
      if ((e.ctrlKey || e.metaKey) && e.key === 'g') {
        e.preventDefault();
        toggleGridUi();
        return;
      }

      // Escape: clear selection
      if (e.key === 'Escape' && (selectedPanelIds.length > 0 || selectedAnnotationId)) {
        if (selectedAnnotationId) {
          setSelectedAnnotationId(null);
        } else {
          clearSelection();
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedPanelIds,
    selectedAnnotationId,
    selectedPanelId,
    currentComposition,
    nudgePanels,
    nudgeAnnotation,
    removePanel,
    removeAnnotation,
    duplicatePanels,
    clearSelection,
    toggleGridUi,
    setSelectedAnnotationId,
  ]);

  const handleShare = async () => {
    console.log('[FigureCanvasWrapper] handleShare called');
    if (!currentComposition) {
      console.warn('[FigureCanvasWrapper] No currentComposition, cannot share');
      return;
    }
    
    // Check if figure is saved (has figureId) - drafts cannot be shared
    const figureId = useFigureCompositionStore.getState().figureId;
    console.log('[FigureCanvasWrapper] figureId:', figureId);
    if (!figureId) {
      toast({
        title: "Save required",
        description: "Please save your figure before sharing. Share links are only available for saved figures.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSharing(true);
    try {
      // Get the full composition from store (not the memoized version) to ensure all fields are included
      const store = useFigureCompositionStore.getState();
      const fullComposition = store.currentComposition;
      if (!fullComposition) {
        throw new Error('No composition available');
      }
      
      // Ensure captionAuto is generated before sharing if it's missing
      if (!fullComposition.captionAuto && fullComposition.panels.some(p => p.plotId)) {
        console.log('[FigureCanvasWrapper] CaptionAuto missing, generating before share...');
        const plotIds = fullComposition.panels
          .filter(p => p.plotId)
          .map(p => p.plotId!)
          .filter(Boolean);
        
        // Load plot captions first if needed
        if (plotIds.length > 0) {
          const { loadInsightsForPlots } = usePlotsStore.getState();
          try {
            await loadInsightsForPlots(fullComposition.projectId, plotIds);
          } catch (err) {
            console.warn('[FigureCanvasWrapper] Failed to load insights for plots:', err);
          }
        }
        
        // Now recompute the caption (async - waits for insights to load)
        await store.recomputeAutoCaption().catch(err => {
          console.warn('[FigureCanvasWrapper] Failed to recompute caption:', err);
          return Promise.resolve(); // Continue even if recompute fails
        });
        // Get updated composition after recompute
        const updatedComposition = useFigureCompositionStore.getState().currentComposition;
        if (updatedComposition) {
          console.log('[FigureCanvasWrapper] After recompute, captionAuto:', updatedComposition.captionAuto?.substring(0, 100));
          Object.assign(fullComposition, updatedComposition);
        }
      }
      
      // Ensure caption fields are included for sharing
      const { getEffectiveCaption } = useFigureCompositionStore.getState();
      const effectiveCaption = getEffectiveCaption();
      const compositionWithCaption = {
        ...fullComposition,
        // Ensure legacy caption field has the effective caption for backwards compatibility
        caption: effectiveCaption,
        // Also include captionManual and captionAuto for shared view
        captionManual: fullComposition.captionManual,
        captionAuto: fullComposition.captionAuto,
      };
      
      console.log('[FigureCanvasWrapper] Final composition for sharing:', {
        hasCaptionAuto: !!compositionWithCaption.captionAuto,
        captionAutoLength: compositionWithCaption.captionAuto?.length || 0,
        captionAutoPreview: compositionWithCaption.captionAuto?.substring(0, 200),
      });
      
      const payload = {
        figureId: figureId,
        currentComposition: compositionWithCaption, // Send full composition with caption fields
        expiryDays: 7
      };
      console.log('[FigureCanvasWrapper] Sending share request with payload:', {
        figureId: payload.figureId,
        panelCount: payload.currentComposition.panels?.length || 0,
        panels: payload.currentComposition.panels?.map((p: any) => ({ id: p.id, plotId: p.plotId })),
        caption: payload.currentComposition.caption?.substring(0, 100),
        captionManual: payload.currentComposition.captionManual?.substring(0, 100),
        captionAuto: payload.currentComposition.captionAuto?.substring(0, 100),
        hasCaption: !!(payload.currentComposition.caption || payload.currentComposition.captionManual || payload.currentComposition.captionAuto)
      });
      
      const response = await authorizedFetch('/api/ai/v2/share-figure', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      console.log('[FigureCanvasWrapper] Share response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        console.error('[FigureCanvasWrapper] Share failed:', errorData);
        throw new Error(errorData.error || `Failed to create share link (${response.status})`);
      }
      
      const result = await response.json();
      console.log('[FigureCanvasWrapper] Share response:', result);
      
      if ((result as any).success) {
        setShareToken((result as any).shareToken);
        setShareModalOpen(true);
        toast({
          title: "Share link created",
          description: "Your figure is now available for sharing.",
        });
      } else {
        throw new Error((result as any).error || "Failed to create share link");
      }
    } catch (error) {
      console.error('[FigureCanvasWrapper] Share error:', error);
      const errorMessage = error instanceof Error ? error.message : "There was an error creating the share link.";
      toast({
        title: "Share failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSharing(false);
    }
  };

  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}/share/${shareToken}`;
    navigator.clipboard.writeText(shareUrl);
    toast({
      title: "Share link copied",
      description: "The share link has been copied to your clipboard.",
    });
    setCopiedShare(true);
    if (copiedShareTimeoutRef.current) window.clearTimeout(copiedShareTimeoutRef.current);
    // @ts-ignore setTimeout returns number in browsers
    copiedShareTimeoutRef.current = window.setTimeout(() => setCopiedShare(false), 1500);
  };

  if (!currentComposition) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Card className="p-12 text-center border-dashed">
          <div className="max-w-md mx-auto">
            <h3 className="text-lg font-semibold mb-2">No multi-panel figure loaded</h3>
            <p className="text-muted-foreground">
              Please select a multi-panel figure to edit.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col" data-testid="figure-canvas-wrapper" ref={wrapperRef}>
      {/* Canvas Toolbar */}
      <div className="bg-card border-b border-border flex items-center justify-between sticky top-0 z-10">
        <CanvasToolbar
          onNavigateToGallery={currentProject?.id ? () => go(routes.project.gallery(currentProject.id)) : undefined}
          onNavigateToFigures={currentProject?.id ? () => go(routes.project.figures.list(currentProject.id)) : undefined}
          canvasWidthMm={currentComposition?.canvasSize.width}
          containerWidthPx={containerWidth}
        />
        <div className="flex items-center gap-2 px-4">
          <TooltipProvider>
            {unsaved && (
              <div className="text-xs text-amber-600 ml-2" data-testid="unsaved-indicator">
                {unsavedLabel || 'Unsaved changes'}
              </div>
            )}
            
            {/* Save */}
            {onSave && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={onSave}
                      disabled={isSaving || isOffline || isEmpty}
                      aria-busy={isSaving}
                      data-testid="button-save"
                    >
                      {isSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {isSaving 
                      ? 'Saving...' 
                      : isOffline 
                        ? 'Cannot save while offline'
                        : isEmpty
                          ? 'Add at least one panel with a plot before saving'
                          : 'Save multi-panel figure'}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
            
            {/* Export */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={() => setExportOpen(true)}
                  data-testid="button-export"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Export</p>
              </TooltipContent>
            </Tooltip>
            
            {/* Share */}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={handleShare}
                    disabled={!currentComposition || !useFigureCompositionStore.getState().figureId || isSharing}
                    aria-busy={isSharing}
                    data-testid="button-share"
                  >
                    {isSharing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Share2 className="h-4 w-4" />
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {isSharing
                    ? "Creating share link..."
                    : !currentComposition || !useFigureCompositionStore.getState().figureId 
                      ? "Save your figure first to generate a share link" 
                      : "Share this figure with others"}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      
      {/* Canvas Content */}
      <div 
        className={`flex-1 overflow-auto ${isDragOver ? 'bg-primary/5' : ''}`}
        onDragOver={handleCanvasDragOver}
        onDragLeave={handleCanvasDragLeave}
        onDrop={handleCanvasDrop}
      >
        {currentComposition.panels.length === 0 ? (
          <div className="p-6">
            <Card className={`p-20 text-center border-dashed ${isDragOver ? 'border-primary border-2' : ''}`}>
              <div className="max-w-2xl mx-auto">
                <h3 className="text-lg font-semibold mb-2">No panels yet</h3>
                <p className="text-muted-foreground mb-4">
                  {isDragOver 
                    ? 'Drop a template here to apply it'
                    : 'Add panels to your multi-panel figure or drag a template here to get started.'}
                </p>
                <p className="text-sm text-muted-foreground">
                  Use the Figure Editor panel on the right to add panels or select a template.
                </p>
              </div>
            </Card>
          </div>
        ) : (
          <div 
            ref={canvasContainerRef}
            className="p-6"
            style={{
              // Scrollable container - doesn't constrain canvas size
              // Canvas will grow based on visual canvas size from FigureCanvas
              minWidth: '100%',
            }}
          >
            <div
              className="inline-block"
              style={{
                transform: previewAtJournalSize ? 'none' : `scale(${canvasZoom})`,
                transformOrigin: 'top left',
                // Width and height will be set by FigureCanvas based on visual canvas size
                // Don't constrain - let FigureCanvas determine its own size
                display: 'block',
              }}
              data-zoom={canvasZoom}
              data-preview-mode={String(previewAtJournalSize)}
            >
              <FigureCanvas 
                composition={currentComposition}
                showRulers={rulersVisible}
                previewAtJournalSize={previewAtJournalSize}
                canvasZoom={canvasZoom}
              />
            </div>
          </div>
        )}
      </div>
      
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} />
      
      {/* Share Modal */}
      <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share Figure</DialogTitle>
            <DialogDescription>
              Create a shareable link to view this figure. The link expires in 7 days.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Share Link</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={`${window.location.origin}/share/${shareToken}`}
                  readOnly
                  className="flex-1"
                  data-testid="input-share-url"
                />
                <Button
                  variant="secondary"
                  data-state={copiedShare ? 'copied' : 'idle'}
                  onClick={copyShareLink}
                  data-testid="button-copy-share-link"
                >
                  {copiedShare ? (
                    <>
                      <Check className="h-4 w-4 mr-1 text-emerald-500" />
                      Copied
                    </>
                  ) : (
                    'Copy'
                  )}
                  <span className="sr-only" aria-live="polite">{copiedShare ? 'Share link copied' : ''}</span>
                </Button>
              </div>
            </div>
            
            <div className="bg-muted rounded p-3 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <Share2 className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Link Details</span>
              </div>
              <ul className="text-muted-foreground space-y-1 text-xs">
                <li>• Read-only interactive view</li>
                <li>• Expires in 7 days</li>
                <li>• No download access</li>
                <li>• Can be revoked anytime</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="secondary" 
              onClick={() => setShareModalOpen(false)}
              data-testid="button-close-share"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

